import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactService } from '../services/contact';
import { queueNewsletterSubscription } from '@/main';
import { Button } from './ui/button';
import { useToast } from './ui/feedback/Toast';

const newsletterSchema = z.object({
  email: z.string().email('Invalid email format'),
});

export function NewsletterForm({ className = '' }) {
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(newsletterSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async ({ email }) => {
    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
    if (offline) {
      try {
        await queueNewsletterSubscription(email);
        showToast('You are offline. We queued your request. We’ll email a confirmation link when you’re back online.', 'info');
        reset();
        return;
      } catch (e) {
        console.warn('Failed to queue offline subscription', e);
      }
    }
    try {
      await contactService.subscribeToNewsletter(email);
      showToast('Check your inbox for a confirmation link to complete your subscription.', 'success');
      reset();
    } catch (error) {
      // If network error, attempt queue
      if (!offline) {
        if (error instanceof Error && /Network|fetch|Failed/.test(error.message) && 'serviceWorker' in navigator) {
          try {
            await queueNewsletterSubscription(email);
            showToast('Network issue. We queued your request and will send a confirmation email once connected.', 'info');
            reset();
            return;
          } catch (qErr) {
            console.warn('Queue attempt failed', qErr);
          }
        }
      }
      console.error('Error subscribing to newsletter:', error);
      showToast(
        error.message === 'Email already subscribed'
          ? 'This email is already subscribed to our newsletter.'
          : 'Failed to subscribe. Please try again later.',
        'error',
      );
    }
  };

  return (
    <form
      noValidate
      aria-label="Newsletter subscription form"
      aria-busy={isSubmitting}
      onSubmit={handleSubmit(onSubmit)}
      className={`flex gap-2 ${className}`}
    >
      <fieldset disabled={isSubmitting} className="flex-1 flex gap-2 p-0 m-0 border-0">
        <label htmlFor="newsletter-email" className="sr-only">
          Email address
        </label>
        <input
          type="email"
          id="newsletter-email"
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? 'newsletter-email-error' : undefined}
          data-testid="newsletter-input"
          {...register('email')}
          placeholder="Enter your email"
          className="w-full rounded-md border bg-background px-3 py-2"
        />
        {errors.email && (
          <p
            id="newsletter-email-error"
            role="alert"
            className="mt-1 text-xs text-red-500"
          >
            {errors.email.message}
          </p>
        )}
        <Button type="submit" data-testid="newsletter-submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <svg
                className="mr-2 h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 
                    0 5.373 0 12h4zm2 5.291A7.962 
                    7.962 0 014 12H0c0 3.042 
                    1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="sr-only">Submitting…</span>
              Subscribing...
            </>
          ) : (
            'Subscribe'
          )}
        </Button>
      </fieldset>
    </form>
  );
}
